import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Signup({ onLogin }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  const trialToken = searchParams.get('trial');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [isTrial, setIsTrial] = useState(false);
  const [step, setStep] = useState('password'); // password, payment, onboarding
  const [subscriptionId, setSubscriptionId] = useState(null);
  const [userId, setUserId] = useState(null);

  useEffect(() => {
    const signupToken = trialToken || token;
    if (!signupToken) {
      toast.error('Invalid invitation link');
      navigate('/app/login');
      return;
    }
    
    // Fetch and validate token to get email
    const validateToken = async () => {
      try {
        const response = await axios.get(`${API}/auth/validate-invite-token?token=${signupToken}`);
        setEmail(response.data.email);
        setIsTrial(response.data.is_trial);
      } catch (error) {
        toast.error('Invalid or expired invitation link');
        navigate('/app/login');
      }
    };
    
    validateToken();
  }, [token, trialToken, navigate]);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (!firstName.trim()) {
      toast.error('Please enter your first name');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);

    try {
      // Use trial token if present, otherwise use regular token
      const signupToken = trialToken || token;
      
      const response = await axios.post(`${API}/auth/complete-signup`, null, {
        params: { 
          token: signupToken, 
          password,
          first_name: firstName,
          last_name: lastName
        }
      });

      console.log('Signup response:', response.data);

      if (!response.data.email) {
        toast.error('Signup failed - missing email');
        return;
      }

      // Log the user in automatically
      const loginResponse = await axios.post(`${API}/auth/login`, {
        email: response.data.email,
        password: password
      });

      console.log('Login response:', loginResponse.data);

      const { access_token, user } = loginResponse.data;
      onLogin(user, access_token);
      
      // All users go through onboarding
      toast.success('Account created! Complete your onboarding.');
      navigate('/app/onboarding');
    } catch (error) {
      console.error('Signup error:', error.response?.data || error);
      toast.error(error.response?.data?.detail || 'Signup failed');
    } finally {
      setLoading(false);
    }
  };

  const handlePayment = async () => {
    setLoading(true);

    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/payments/create-checkout`, {
        subscription_id: subscriptionId,
        origin_url: originUrl
      });

      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error('Payment initialization failed');
      setLoading(false);
    }
  };

  if (step === 'password') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md" data-testid="signup-card">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-bold">
              {isTrial ? '🎉 Start Your Free Trial' : 'Create Your Account'}
            </CardTitle>
            <CardDescription>
              {isTrial ? '7 days of full access - no credit card required' : 'Complete your details to get started'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  readOnly
                  className="bg-gray-50 cursor-not-allowed"
                  data-testid="email-input"
                />
                <p className="text-xs text-gray-500">This email is linked to your invitation</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="John"
                    required
                    data-testid="firstname-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Doe"
                    data-testid="lastname-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  required
                  data-testid="password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Confirm Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-[#1a61e8] to-[#1552c7]"
                disabled={loading}
                data-testid="set-password-button"
              >
                {loading ? 'Creating Account...' : isTrial ? 'Start Free Trial' : 'Create Account'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 'payment') {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="w-full max-w-md" data-testid="payment-card">
          <CardHeader className="text-center">
            <CardTitle className="text-3xl font-bold">Complete Payment</CardTitle>
            <CardDescription>Subscribe to access your personalized meal plans</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="text-center py-6">
                <div className="text-5xl font-bold text-[#1a61e8] mb-2">$99</div>
                <div className="text-gray-600">per month</div>
              </div>
              <Button
                onClick={handlePayment}
                className="w-full bg-gradient-to-r from-[#1a61e8] to-[#1552c7] text-lg py-6"
                disabled={loading}
                data-testid="proceed-to-payment-button"
              >
                {loading ? 'Processing...' : 'Proceed to Payment'}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
}
